<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
        <div class="col-sm-12">
          <h3>{{ messageTitle }}</h3>
          <div class="form-group languageversioned" v-if="hasSubjectField">
            <label for="subject">{{ _('Message subject') }}</label>
            <input type="text" class="form-control" id="subject" v-model="message.subject" />
          </div>
          <div class="form-group languageversioned">
            <label for="text">{{ _('Message text') }}</label>
            <editor v-model="message.text" v-if="hasRichTextEditor" />
            <textarea v-else class="form-control" wrap="virtual" rows="15" v-model="message.text"></textarea>
          </div>
          <p class="text-info">
            <i class="fa fa-fw fa-info"></i>
            <span v-html="messageDescription"></span>
          </p>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-info" v-on:click="translateDialog" type="button" v-if="isTranslateButtonVisible" :disabled="!isTranslateButtonEnabled"><i class="fa fa-fw fa-language"></i><span class="hidden-xs"> {{ _('Translate') }}</span></button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import messagetypes from '~/lib/messagetypes'
import settingshelpers from '~/lib/settingshelpers'
import { Editor, TranslationSelector } from '#components'

export default {
  data () {
    return {
      message: {}
    }
  },
  mixins: [changewatcher, components, messagetypes, settingshelpers],
  computed: {
    isTranslateButtonVisible () {
      return this.isFeatureEnabled('translation')
    },
    isTranslateButtonEnabled() {
      return this.locale !== this.version
    },
    messageTitle() {
      if (!this.message) {
        return this._('Message')
      }
      return this._(this.getMessageTitle(this.message.type))
    },
    messageDescription() {
      if (!this.message) {
        return ''
      }
      let desc = this.getMessageDescription(this.message.type)
      if (desc.constructor === Array) {
        return desc.map(d => this._(d)).join(' ')
      } else {
        return this._(desc)
      }
    },
    hasSubjectField() {
      if (!this.message) {
        return false
      }
      switch (this.message.type) {
        case 'termsDescription':
          return false
      }
      return true
    },
    hasRichTextEditor() {
      return this.getMessageEditor(this.message.type) === 'richEditor'
    }
  },
  methods: {
    translateDialog () {
      const $this = this
      this.showModal('translationselector', TranslationSelector, {
        props: {
          title: this._('Select texts to be translated'),
          settings: this.settings,
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              modal: true
            }
          },
          parent: this.message,
          parentType: 'messages',
          className: 'modal-content'
        },
        events: {
          result (data) {
            for (let i = 0; i < data.length; i++) {
              const d = data[i]
              $this.message[d.field] = d.text
            }
          }
        },
        handlers: {},
        width: 1000
      })
    },
    getSaveData () {
      const exportFields = [
        'id',
        'subject',
        'text',
        'type'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.message[e], a), {})
      data.version = this.version
      return data
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.type) {
          response = await axios.put(this.baseDomain + this.shopId + '/messages/' + data.type + '.json?apikey=' + this.apiKey, data)
        } else {
          return false
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data) {
        await this.loadMessage(data.type)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async reloadOnVersionChange () {
      if (!this.message || !this.message.type) {
        return
      }
      await this.loadMessage(this.message.type)
    },
    loadMessage (type) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/messages/' + type + '.json?apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.loading = false
          this.message = {
            type, 
            subject: '',
            text: ''
          }
          return
        }
        this.message = response.data
        this.storeState()
        this.loading = false
        resolve(this.message)
      })
    }
  },
  components: {
    Editor
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadMessage(this.component.initData.type)
    }
  },
  mounted () {
    if(!this.events) {
      let events = {}
      events['messageUpdate'] = async (message) => {
        if (message && message.data && this.message && message.data.type === this.message.type) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadMessage(message.data.type)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.message && this.message.type && this.message.type !== this.component.initData.type) {
        try {
          await this.loadMessage(this.component.initData.type)
        } catch(e) {}
      }
    }
  }
}
</script>